import { useMemo } from "react";
import { cn } from "@/lib/cn";
import { fmtMoneyExact } from "@/lib/cost";
import { EBITDA_DEFINITION, annualBenefitTotal, asRealized, inYearBenefitTotal, inYearEbitdaDefinition } from "@/lib/benefit";
import type { Project } from "@/types/project";

/**
 * The board scoreboard — three cards that set the meeting agenda: At
 * Risk, Annual EBITDA and this year's EBITDA. Shared by /executive and
 * /top-projects (Brett, 2026-09-18: "both pages") so the two can never
 * disagree: same rows in, same math.
 *
 * `rows` = the live (non-complete) projects the At Risk card counts:
 * every decided band on /top-projects, the Top 10 only on /executive
 * (Brett, 2026-09-21). The At Risk card is a FILTER toggle the host
 * applies to its own surface — bands on the board, Gantt rows on the
 * exec page.
 *
 * 2026-11-04 (Brett): the board remembers the Top 10 was to deliver
 * ~$2.3M. A project that COMPLETES keeps its EBITDA on the cards —
 * `delivered` rows add to the EBITDA numbers (shown as delivered ·
 * in flight) but never to the At Risk count, which stays about live work.
 *
 * 2026-11-04, later (Brett): three cards. On Track and Go-Lives · 30
 * Days are retired; EBITDA gets two cards — Annual and this year's —
 * and each shows its total with what has been delivered.
 *
 * 2026-11-04, later still (Brett): "only focus on EBITDA for the Top
 * 10". Both pages pass the Top 10 as `ebitdaRows` / `delivered`, so the
 * EBITDA cards read the same on each. At Risk on /top-projects still
 * covers every decided band, because it filters the whole board.
 */

const YEAR = new Date().getFullYear();

/** A paused project is a decision the board made, not a delivery
 * problem — it never counts as at risk or on track (Brett, 2026-09-22). */
export function isPostponed(p: Project): boolean {
  return (p.status ?? "").toLowerCase() === "postponed";
}

export function isAtRisk(p: Project): boolean {
  return !isPostponed(p) && ["red", "yellow"].includes((p.health ?? "").toLowerCase());
}

/**
 * "Delivered" projects — the ones whose EBITDA stays in the totals after
 * they finish (Brett, 2026-11-04). A project counts when BOTH hold:
 *   1. its status is Complete, and
 *   2. it still carries a decided band (Top 10, Just Do, Next up, Paused).
 * New / unscoped is the intake queue, not committed work, and a project
 * with no band was never on the board — neither counts. Pass `band` to
 * keep one band only (the Executive page and each band's own total).
 */
export function deliveredBoardProjects(projects: Project[], band?: string): Project[] {
  return projects.filter((project) => {
    const isComplete = (project.status ?? "").toLowerCase() === "complete";
    const projectBand = project.priority_tier;
    if (!isComplete || !projectBand || projectBand === "unscoped") return false;
    return band ? projectBand === band : true;
  });
}

/**
 * Numbers behind the three cards (Brett, 2026-11-04).
 *   rows       — live projects the At Risk card counts.
 *   ebitdaRows — live projects whose EBITDA is still in flight
 *                (defaults to `rows`).
 *   delivered  — completed projects whose EBITDA stays in both totals.
 * Each EBITDA total is split into delivered and in flight.
 */
export function computeBoardStats({
  rows,
  ebitdaRows = rows,
  delivered = [],
  year = YEAR,
}: {
  rows: Project[];
  ebitdaRows?: Project[];
  delivered?: Project[];
  year?: number;
}) {
  const annualInFlight = annualBenefitTotal(ebitdaRows);
  const annualDelivered = annualBenefitTotal(delivered);
  const inYearInFlight = inYearBenefitTotal(ebitdaRows, year);
  const inYearDelivered = inYearBenefitTotal(asRealized(delivered), year);
  return {
    total: rows.length,
    atRisk: rows.filter(isAtRisk).length,
    benefit: annualInFlight + annualDelivered,
    benefitDelivered: annualDelivered,
    benefitInFlight: annualInFlight,
    benefitInYear: inYearInFlight + inYearDelivered,
    benefitInYearDelivered: inYearDelivered,
    benefitInYearInFlight: inYearInFlight,
    deliveredCount: delivered.length,
    year,
  };
}

/** One EBITDA card: the total, then how much of it is delivered. */
function EbitdaCard({
  label,
  labelHelp,
  total,
  delivered,
  inFlight,
  caption,
  testId,
}: {
  label: string;
  labelHelp: string;
  total: number;
  delivered: number;
  inFlight: number;
  caption: string;
  testId: string;
}) {
  const deliveredShare = total > 0 ? Math.min(100, Math.round((delivered / total) * 100)) : 0;
  return (
    <div className="relative overflow-hidden rounded-2xl border bg-surface p-5 pl-6 shadow-sm ring-1 ring-line" data-testid={`${testId}-card`}>
      <div className="absolute bottom-0 left-0 top-0 w-[5px] bg-emerald-500" />
      <div className="w-fit cursor-help text-xs font-bold uppercase tracking-wider text-ink-muted underline decoration-dotted underline-offset-2" title={labelHelp} data-testid={`${testId}-label`}>
        {label}
      </div>
      <div className="mt-2 text-4xl font-extrabold tabular-nums leading-none text-emerald-700 dark:text-emerald-300" data-testid={testId}>
        {fmtMoneyExact(total)}
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-raised" title={`${deliveredShare}% delivered`}>
        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${deliveredShare}%` }} data-testid={`${testId}-bar`} />
      </div>
      <div className="mt-2 text-sm text-ink-2" data-testid={`${testId}-split`} title="Completed projects keep their EBITDA in the total — it doesn't shrink when a project delivers">
        <span className="font-bold tabular-nums text-emerald-700 dark:text-emerald-300">{fmtMoneyExact(delivered)}</span> delivered
        <span className="text-ink-subtle"> · </span>
        <span className="font-semibold tabular-nums">{fmtMoneyExact(inFlight)}</span> in flight
      </div>
      <div className="mt-1 text-xs text-ink-muted">{caption}</div>
    </div>
  );
}

export function BoardKpiCards({
  rows,
  atRiskOnly,
  onToggleAtRisk,
  scopeLabel = "on the board",
  ebitdaRows,
  delivered = [],
}: {
  rows: Project[];
  atRiskOnly: boolean;
  onToggleAtRisk: () => void;
  /** Where the EBITDA numbers come from, e.g. "on the board" / "in the Top 10". */
  scopeLabel?: string;
  /** Live projects the EBITDA cards add up. Defaults to `rows`. */
  ebitdaRows?: Project[];
  /** Completed projects in the EBITDA scope: their EBITDA stays on the cards. */
  delivered?: Project[];
}) {
  const stats = useMemo(() => computeBoardStats({ rows, ebitdaRows, delivered }), [rows, ebitdaRows, delivered]);
  const ofTotal = <span className="text-lg font-semibold text-ink-subtle"> of {stats.total}</span>;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="board-kpis">
      <button
        type="button"
        onClick={onToggleAtRisk}
        data-card="at-risk"
        className={cn(
          // flex + justify-start: a <button> centers its content vertically,
          // which shows whenever a neighbouring card is taller.
          "relative flex flex-col justify-start overflow-hidden rounded-2xl border bg-surface p-5 pl-6 text-left shadow-sm ring-1 transition-all",
          atRiskOnly ? "ring-2 ring-amber-400 border-amber-300 dark:border-amber-500/40" : "ring-line hover:shadow-md",
        )}
        title={atRiskOnly ? "Show all rows" : "Show only red/yellow rows"}
      >
        <div className={cn("absolute bottom-0 left-0 top-0 w-[5px]", stats.atRisk > 0 ? "bg-amber-400" : "bg-emerald-400")} />
        <div className="text-xs font-bold uppercase tracking-wider text-ink-muted">At Risk</div>
        <div className="mt-2 text-4xl font-extrabold tabular-nums leading-none text-ink" data-testid="kpi-at-risk">
          {stats.atRisk}
          {ofTotal}
        </div>
        <div className="mt-2 text-xs text-ink-muted">
          {atRiskOnly ? "Filtering — click to show all" : "Red or yellow health — click to focus the meeting"}
        </div>
      </button>
      <EbitdaCard
        label="Annual EBITDA"
        labelHelp={EBITDA_DEFINITION}
        total={stats.benefit}
        delivered={stats.benefitDelivered}
        inFlight={stats.benefitInFlight}
        caption={`Per full year once live, ${scopeLabel}`}
        testId="kpi-benefit"
      />
      <EbitdaCard
        label={`${stats.year} EBITDA`}
        labelHelp={inYearEbitdaDefinition(stats.year)}
        total={stats.benefitInYear}
        delivered={stats.benefitInYearDelivered}
        inFlight={stats.benefitInYearInFlight}
        caption={`Captured in ${stats.year} ${scopeLabel}, prorated from each go-live date`}
        testId="kpi-in-year"
      />
    </div>
  );
}
